import 'swiper/css';

import { Children, type ReactNode, useState } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';
import { A11y, Keyboard } from 'swiper/modules';

import ChevronLeftIcon from '@/assets/svg/ui-elements/chevron-left.svg';
import ChevronRightIcon from '@/assets/svg/ui-elements/chevron-right.svg';
import { IconButton } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { MOBILE_PAGE_PADDING } from '@/pages/portfolio/portfolio.styled';

import {
  CarouselControls,
  CarouselHeader,
  CarouselRoot,
  StyledSwiper,
  StyledSwiperSlide,
} from './carousel.styled';

interface CarouselProps {
  ariaLabel: string;
  header?: ReactNode;
  children?: ReactNode;
  emptyState?: ReactNode;
  loop?: boolean;
  showNavigation?: boolean;
  isLoading?: boolean;
  spaceBetween?: number;
}

export const Carousel = ({
  ariaLabel,
  header,
  children,
  emptyState,
  loop = false,
  showNavigation = true,
  isLoading = false,
  spaceBetween = 12,
}: CarouselProps) => {
  const isMobile = useIsMobile();
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null);

  const edgeOffset = isMobile ? MOBILE_PAGE_PADDING : 0;
  const slides = Children.toArray(children);
  const hasSlides = slides.length > 0;
  const hasControls = showNavigation && !isLoading && hasSlides;

  return (
    <CarouselRoot aria-roledescription="carousel" aria-label={ariaLabel} aria-busy={isLoading}>
      <CarouselHeader>
        {header}

        {hasControls && (
          <CarouselControls>
            <IconButton
              size="md"
              variant="outline"
              color="tertiary"
              aria-label={`Previous slide in ${ariaLabel}`}
              onClick={() => swiper?.slidePrev()}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="md"
              variant="outline"
              color="tertiary"
              aria-label={`Next slide in ${ariaLabel}`}
              onClick={() => swiper?.slideNext()}
            >
              <ChevronRightIcon />
            </IconButton>
          </CarouselControls>
        )}
      </CarouselHeader>

      {!hasSlides && emptyState}

      {hasSlides && (
        <StyledSwiper
          modules={[A11y, Keyboard]}
          slidesPerView="auto"
          spaceBetween={spaceBetween}
          slidesOffsetBefore={edgeOffset}
          slidesOffsetAfter={edgeOffset}
          loop={loop}
          keyboard={{ enabled: true, onlyInViewport: true }}
          a11y={{
            containerRoleDescriptionMessage: 'carousel',
            itemRoleDescriptionMessage: 'slide',
          }}
          onSwiper={setSwiper}
        >
          {slides.map((slide, index) => (
            <StyledSwiperSlide key={index}>{slide}</StyledSwiperSlide>
          ))}
        </StyledSwiper>
      )}
    </CarouselRoot>
  );
};
